<template>
  <div v-if="initialized" class="product-infos">
    <div class="label font-weight-bold">
      Your logo:
    </div>
    <div class="section">
      <v-switch :disabled="!isAvailable('editor:scan-result:logo')" v-model="enableLogo" :label="`Display the logo banner: ${enableLogo ? 'Yes' : 'No'}`"></v-switch>
      <div v-if="enableLogo">
        <div class="d-flex flex-row inset">
          <ImageCropper v-if="!logo" input-key="logo" @image-upload="onImageUpload"></ImageCropper>
          <div class="logo-ctn ml-3 d-flex flex-row align-center" v-if="logo">
            <img :src="logo">
            <v-btn @click="deleteLogo" icon>
              <v-icon>$remove</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="inset font-weight-light pt-4">
          Supported Format: PNG without background color / JPG with pure white background<br>
          Use square or horizontal version of your logo.
        </div>
      </div>

    </div>
    <div class="label font-weight-bold">
      Product picture:
    </div>
    <div class="section">
      <div class="inset font-weight-light pt-4">
        The product picture will be automatically fetched from the cloud. You can go to product list to manage the name and picture for each product.
      </div>
      <div v-if="isAvailable('editor:scan-result:display-mode')">
        <div class="inset label font-weight-medium pt-4">
          Display mode
        </div>
        <v-radio-group v-model="productImageDisplayMode" class="inset" column>
          <v-radio value="contain" label="Contain" class="pa-0 ma-0">
          </v-radio>
          <span class="font-weight-light pl-8 pb-2">Best suited for product image that have a white background.</span>
          <v-radio value="cover" label="Cover" class="pa-0 ma-0">
          </v-radio>
          <span class="font-weight-light pl-8 pb-2">Best suited for product image with non-white background.</span>
          <v-radio value="full-width" label="Full-width" class="pa-0 ma-0">
          </v-radio>
          <span class="font-weight-light pl-8">This will scale your picture to the phone width increasing the overall size.</span>
        </v-radio-group>
      </div>
    </div>
    <div class="label font-weight-bold">
      Scan Result area:
    </div>
    <div class="section">
      <v-switch :disabled="!isAvailable('editor:scan-result:text')" class="ma-0 pa-0 pt-4" hide-details v-model="overrideAuthMessage" :label="`Customize Scan Result text: ${overrideAuthMessage ? 'Yes' : 'No'}`"></v-switch>
      <div class="inset" v-if="overrideAuthMessage">
        <div class="table pa-3">
          <div class="d-flex flex-row table-header">
            <div class="item font-weight-medium text-caption">Standard text</div>
            <div class="item font-weight-medium text-caption">Personalized text</div>
            <!-- <div class="item font-weight-medium text-caption">icon</div> -->
          </div>
            <div v-if="isAvailable('editor:authentication')" class="d-flex flex-row flex-grow-1 table-row pt-2">
              <div class="item py-2 font-weight-bold">Genuine</div>
              <div class="item flex-grow-1">
                <v-text-field class="ma-0 pa-0 pr-6" v-model="authMessageOverride.genuine" hide-details placeholder="Enter a personalized text"></v-text-field>
              </div>
              <!-- <div class="item font-weight-bold">Unable To Verify</div> -->
            </div>

            <div class="d-flex flex-row flex-grow-1 table-row pt-2">
              <div class="item py-2 font-weight-bold">Active Code</div>
              <div class="item flex-grow-1">
                <v-text-field class="ma-0 pa-0 pr-6" v-model="authMessageOverride.active" hide-details placeholder="Enter a personalized text"></v-text-field>
              </div>
              <!-- <div class="item font-weight-bold">Unable To Verify</div> -->
            </div>

            <div v-if="isAvailable('editor:authentication')" class="d-flex flex-row flex-grow-1 table-row pt-2">
              <div class="item py-2 font-weight-bold">Unable To Verify</div>
              <div class="item flex-grow-1">
                <v-text-field class="ma-0 pa-0 pr-6" v-model="authMessageOverride.fake" hide-details placeholder="Enter a personalized text"></v-text-field>
              </div>
              <!-- <div class="item font-weight-bold">Unable To Verify</div> -->
            </div>
        </div>

      </div>

      <v-switch :disabled="!isAvailable('editor:scan-result:branding')" class="ma-0 pt-4" hide-details v-model="enableBranding" :label="`Display the Scantrust brand text: ${enableBranding ? 'Yes' : 'No'}`"></v-switch>
      <div class="inset font-weight-light">
        "Authentication powered by Scantrust"
      </div>
      <v-switch :disabled="!isAvailable('editor:scan-result:serial-number')" class="ma-0 pt-4" hide-details v-model="showSerialNumber" :label="`Display serial number: ${showSerialNumber ? 'Yes' : 'No'}`"></v-switch>

    </div>
  </div>
</template>

<script>

import ImageCropper from '@/components/utils/ImageCropper'
import { mapGetters, mapMutations } from 'vuex'

export default {
  name: 'ScanResultTab',
  components: {
    ImageCropper
  },
  watch: {
    authMessageOverride: {
      deep: true,
      handler (messages) {
        this.$store.dispatch('updateTabAuthMessage', messages)
      }
    },
    initialized (newVal) {
      if (newVal) {
        this.authMessageOverride = this.authMessage
        this.overrideAuthMessage = this.isUsingCustomAuthMessage()
        this.enableLogo = !!(this.logo && this.logo.length)
      }
    },

    enableLogo (newVal) {
      if (!newVal) {
        this.deleteLogo()
      }
    },

    overrideAuthMessage (newVal) {
      if (!newVal) {
        this.authMessageOverride = { genuine: '', active: '', fake: '' }
        this.$store.dispatch('updateTabAuthMessage', this.authMessageOverride)
      }
    }
  },
  computed: {
    ...mapGetters(['authMessage', 'logo', 'initialized', 'isAvailable']),
    showSerialNumber: {
      get () {
        return this.$store.getters.showSerialNumber
      },
      set (value) {
        this.setSerialDisplay(value)
      }
    },

    productImageDisplayMode: {
      get () {
        return this.$store.getters.productImageDisplayMode
      },
      set (value) {
        this.setDisplayMode(value)
      }
    },

    enableBranding: {
      get () {
        return this.$store.getters.enableBranding
      },
      set (value) {
        this.setBrandingMode(value)
      }
    }
  },
  data () {
    return {
      authMessageOverride: {},
      enableLogo: false,
      overrideAuthMessage: false,
      enableMessage: false,
      customGenuine: 'hello'
    }
  },
  methods: {

    ...mapMutations({
      setSerialDisplay: 'SET_SERIAL_NUMBER_DISPLAY',
      setDisplayMode: 'SET_PRODUCT_DISPLAY_MODE',
      setBrandingMode: 'SET_BRANDING_DISPLAY_MODE'
    }),

    isUsingCustomAuthMessage () {
      for (const [, val] of Object.entries(this.authMessageOverride)) {
        if (val && val.length) {
          return true
        }
      }
      return false
    },

    onImageUpload (url) {
      this.$store.dispatch('updateLogo', url)
    },

    deleteLogo () {
      this.$store.dispatch('updateLogo', '')
    }
  }
}

</script>

<style lang="sass" scoped>

@import '@/variables.sass'

.section
  padding: 0px 0px 20px 0px

.product-infos
  padding: 20px 30px

.label
  text-transform: uppercase
  color: $label-grey

::v-deep label
  color: black !important

.inset
  padding-left: 46px

.logo-ctn
  img
    height: 50px

</style>
